import type { AppliesTo, Product, ProductDraft } from '@lemontaigu/pms-core';
import { parseEurosToCents } from './logic.js';

// The catalogue form's STATE and its payload builder — the pure half of
// « Prestations », so the arithmetic and the rules that decide what reaches the
// database are testable without a browser (the `reservationDraft` shape).
//
// ── Why this validates at all, when the database already does ───────────────
//
// Every rule below is ALSO a CHECK on `pms_product`, and the database is the
// enforcer (ARCHITECTURE.md §1). What this buys is the thing a constraint
// cannot: a French sentence naming the field, before a round trip. A raw
// `pms_product_sale_states_tva` violation surfaced to an operator entering a
// breakfast is not a message, it is a puzzle.
//
// The mirror is deliberate and the cost is stated: if a CHECK changes and this
// does not, the form starts refusing something the database would accept — the
// safe direction of that failure, and the reason these are phrased as the
// database's rules rather than as extra opinions of our own.

export type ProductForm = {
  /** The stored id, or null when creating. */
  id: number | null;
  code: string;
  labelFr: string;
  descriptionFr: string;
  emoji: string;
  kind: Product['kind'];
  appliesTo: AppliesTo;
  perPerson: boolean;
  priceEuros: string;
  /** As typed: « 10 », « 5,5 ». Empty means none, which is legal ONLY for a
   *  tax — a levy collected for a commune is not a sale and bears no TVA. */
  tvaPercent: string;
  isTax: boolean;
  autoApply: boolean;
  manualQty: boolean;
  sortOrder: string;
  /** A child meal bound to a band: its price is the published rate profile's
   *  (PMS-PHONE-BOOKING-BRIEF §4.2), so the form shows it and never edits it —
   *  the database refuses a typed one (pms_product_profile_price_guard). */
  priceFromProfile: boolean;
};

export const emptyProduct = (): ProductForm => ({
  id: null,
  code: '',
  labelFr: '',
  descriptionFr: '',
  emoji: '',
  kind: 'service',
  // The commonest extra by far is a per-person breakfast, so the form opens on
  // the shape the operator most often wants rather than on a blank slate.
  appliesTo: 'each_morning',
  perPerson: true,
  priceEuros: '',
  tvaPercent: '10',
  isTax: false,
  autoApply: false,
  manualQty: true,
  sortOrder: '100',
  priceFromProfile: false,
});

export const productToForm = (p: Product): ProductForm => ({
  id: p.id,
  code: p.code,
  labelFr: p.labelFr,
  descriptionFr: p.descriptionFr ?? '',
  emoji: p.emoji ?? '',
  kind: p.kind,
  appliesTo: p.appliesTo,
  perPerson: p.perPerson,
  priceEuros: (p.priceCents / 100).toFixed(2).replace('.', ','),
  tvaPercent: p.tvaRate === null ? '' : String(p.tvaRate).replace('.', ','),
  isTax: p.isTax,
  autoApply: p.autoApply,
  manualQty: p.manualQty,
  sortOrder: String(p.sortOrder),
  priceFromProfile: p.profileBand != null,
});

/** A percentage as typed → a number, or null for « none ». Rejects nonsense by
 *  returning undefined, which the builder turns into a named error. */
export function parsePercent(text: string): number | null | undefined {
  const t = text.trim();
  if (t === '') return null;
  const n = Number(t.replace(',', '.'));
  if (!Number.isFinite(n) || n < 0 || n > 100) return undefined;
  return n;
}

/**
 * Form → what the service takes, or a French error naming what is wrong.
 *
 * Returning the error rather than throwing keeps this pure and keeps the caller
 * honest: there is no path where an invalid form silently produces a payload.
 */
export function buildProductDraft(
  f: ProductForm,
): { ok: true; draft: ProductDraft } | { ok: false; error: string } {
  const code = f.code.trim().toUpperCase();
  if (code === '') return { ok: false, error: 'Le code est obligatoire (ex. PDJ, PARKING).' };
  if (!/^[A-Z0-9_]+$/.test(code)) {
    // It is the stable key charges are written against and the string an OTA
    // service will one day be mapped to, so it stays machine-shaped.
    return { ok: false, error: 'Le code ne peut contenir que des lettres, chiffres et « _ ».' };
  }
  if (f.labelFr.trim() === '') return { ok: false, error: 'Le libellé est obligatoire.' };

  const priceCents = parseEurosToCents(f.priceEuros);
  if (priceCents === null) return { ok: false, error: 'Prix invalide (ex. 18 ou 18,50).' };

  const tva = parsePercent(f.tvaPercent);
  if (tva === undefined) return { ok: false, error: 'Taux de TVA invalide (0 à 100).' };

  // The two halves of the same rule, stated as the database states them.
  if (f.isTax && tva !== null) {
    return {
      ok: false,
      error: 'Une taxe de séjour ne porte pas de TVA : elle est collectée pour la commune, ce n’est pas une vente.',
    };
  }
  if (!f.isTax && tva === null) {
    return { ok: false, error: 'Un article vendu doit indiquer son taux de TVA.' };
  }

  const sortOrder = Number(f.sortOrder.trim());
  if (!Number.isFinite(sortOrder)) return { ok: false, error: 'Ordre d’affichage invalide.' };

  return {
    ok: true,
    draft: {
      ...(f.id === null ? {} : { id: f.id }),
      code,
      labelFr: f.labelFr.trim(),
      descriptionFr: f.descriptionFr.trim() === '' ? null : f.descriptionFr.trim(),
      kind: f.kind,
      emoji: f.emoji.trim() === '' ? null : f.emoji.trim(),
      // A taxe de séjour is per person per night by law and the database
      // refuses any other shape, so the form does not offer one. Forcing it
      // here rather than disabling two selects keeps « what a tax IS » in one
      // place instead of spread across the markup.
      appliesTo: f.isTax ? 'each_night' : f.appliesTo,
      perPerson: f.isTax ? true : f.perPerson,
      priceCents,
      tvaRate: tva,
      isTax: f.isTax,
      autoApply: f.autoApply,
      manualQty: f.isTax ? false : f.manualQty,
      sortOrder,
    },
  };
}

/** How a product reads in one line — « 18,00 € / pers. / matin · TVA 10 % ». */
export function describeProduct(p: Product): string {
  const bits: string[] = [(p.priceCents / 100).toFixed(2).replace('.', ',') + ' €'];
  if (p.perPerson) bits.push('/ pers.');
  if (p.appliesTo === 'each_night') bits.push('/ nuit');
  if (p.appliesTo === 'each_morning') bits.push('/ matin');
  if (p.appliesTo === 'arrival') bits.push("à l'arrivée");
  if (p.appliesTo === 'departure') bits.push('au départ');
  const tail = p.isTax ? 'sans TVA' : `TVA ${String(p.tvaRate ?? 0).replace('.', ',')} %`;
  return `${bits.join(' ')} · ${tail}`;
}
